import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Link } from 'react-router-dom';

export const About: React.FC = () => {
  const { surfaceClass } = useTheme();

  return (
    <div className="pb-24 px-6 pt-12 max-w-3xl mx-auto animate-in fade-in duration-500 space-y-10">
      <div className="text-center space-y-4">
        <h1 className="font-serif text-4xl font-bold">About Readsy Link</h1>
        <p className="text-base opacity-70 leading-relaxed">
          The name is Readsy Link. First I started writing. Then I turned the platform into part of the story.
        </p>
        <p className="text-base opacity-70 leading-relaxed">
          What you see here isn’t just a website. It’s a long-running performance: an open lab where I write, and everyone else is invited to do the same.
        </p>
        <p className="text-base opacity-70 leading-relaxed">
          Some people come here to read. Some come to leave their own trace. Both are part of the piece.
        </p>
      </div>

      <Section title="What This Place Is" surfaceClass={surfaceClass}>
        <p>On paper, Readsy Link is an online space for stories, case files, and investigations.</p>
        <p className="mt-3">
          In practice, it’s a simple question: <strong>what happens if you treat a reading platform as an ongoing investigation, not a product?</strong>
        </p>
        <div className="mt-4 space-y-2">
          <p className="font-bold">I use it to:</p>
          <ul className="list-disc list-inside space-y-1 text-sm opacity-80">
            <li>publish my own crime stories and social noir, often built on real patterns,</li>
            <li>map how people, systems, and institutions collide,</li>
            <li>leave fragments in public spaces — reviews, notes, lines that don’t behave like normal comments.</li>
          </ul>
          <p className="font-bold mt-4">You use it to:</p>
          <ul className="list-disc list-inside space-y-1 text-sm opacity-80">
            <li>read,</li>
            <li>bookmark what hits too close,</li>
            <li>publish your own work if you want to step into the same room.</li>
          </ul>
          <p className="mt-4">The platform is the stage. The text is the performance. The readers are the witnesses.</p>
        </div>
      </Section>

      <Section title="My Role vs. The Platform" surfaceClass={surfaceClass}>
        <p>I’m the author. The platform is my way of writing in public.</p>
        <p className="mt-3">
          When you see “Readsy Link” on a page, it’s me. When you see “Readsy Link” in the header, it’s the space I built to hold everything:
        </p>
        <ul className="list-disc list-inside space-y-1 text-sm opacity-80 mt-2">
          <li>my files,</li>
          <li>your files,</li>
          <li>unfinished thoughts,</li>
          <li>quiet investigations that never made it into official reports.</li>
        </ul>
        <p className="mt-3">There’s no big media company behind it. If it feels personal, it’s because it is.</p>
      </Section>

      <Section title="How the Space Behaves" surfaceClass={surfaceClass}>
        <p>The design stays simple on purpose.</p>
        <ul className="list-disc list-inside space-y-1 text-sm opacity-80 mt-2">
          <li>Text in the center, everything else in the background.</li>
          <li>You can adjust type, spacing, and themes so long sessions don’t punish you.</li>
          <li>No blinking ads, no “you might also like” traps, no growth hacks dressed as features.</li>
        </ul>
        <p className="mt-3">Most platforms compete for your attention. This one assumes you still know how to focus.</p>
      </Section>

      <Section title="Magic Login" surfaceClass={surfaceClass}>
        <p>You don’t need another password to forget.</p>
        <p className="mt-2">You can read without an account. If you decide you want to keep your trail, I send a magic link to your email so you can:</p>
        <ul className="list-disc list-inside space-y-1 text-sm opacity-80 mt-2">
          <li>continue where you stopped,</li>
          <li>access your archive and bookmarks,</li>
          <li>publish your own case files.</li>
        </ul>
        <p className="mt-3">No usernames, no security questions, no “prove you’re not a robot” mini-game.</p>
      </Section>

      <Section title="Any Device, Same Story" surfaceClass={surfaceClass}>
        <p>Start on your phone, continue on a laptop, finish somewhere else.</p>
        <p className="mt-2">Your progress syncs quietly so you don’t have to remember chapter numbers or which device you used last. That’s the system’s job, not yours.</p>
      </Section>

      <Section title="Open Starts" surfaceClass={surfaceClass}>
        <p>You can read the start of any public case file for free.</p>
        <p className="mt-2">If it’s not your story, close it. If it is, follow it all the way through.</p>
        <p className="mt-2">No credit card wall. No countdown timer telling you when to care.</p>
      </Section>

      <Section title="Why It Exists" surfaceClass={surfaceClass}>
        <p>Because too many important things get wrapped in phrases like:</p>
        <ul className="list-disc list-inside space-y-1 text-sm opacity-80 mt-2">
          <li>“unfortunate incident”,</li>
          <li>“internal matter”,</li>
          <li>“no further comment at this time”.</li>
        </ul>
        <p className="mt-3">
          Stories last longer than statements. So I write, and I built a place where others can write too. If you’ve ever looked at some “official version” and thought, <strong>“there’s more to it than this”</strong>, you’re already inside the performance.
        </p>
      </Section>

      <div className="text-center">
        <Link to="/search" className="inline-block bg-brand-blue text-white px-8 py-3 rounded-full font-bold shadow-lg hover:shadow-xl hover:bg-brand-hover transition-all">
          Browse Case Files
        </Link>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; surfaceClass: string; children: React.ReactNode }> = ({ title, surfaceClass, children }) => (
  <section className={`p-6 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm ${surfaceClass}`}>
    <h2 className="font-serif text-2xl font-bold mb-3">{title}</h2>
    <div className="space-y-2 text-sm opacity-80">{children}</div>
  </section>
);
